import { ChangeEventHandler, FC, KeyboardEventHandler, useEffect, useState } from 'react';
import {
  ALLOWED_WIDGET_HOSTS,
  AdElementWidget,
  WidgetUrlRejection,
  resolveWidgetUrl,
} from '@adflow/types';
import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react';
import { useStore } from '../../../hooks/useStore';
import { showsAdEvent, takesEvent, withAdEvent } from './adEvent';

const URL_PROBLEMS: Record<WidgetUrlRejection, string> = {
  'empty-url': 'Paste the widget link. The template cannot be saved without it.',
  'invalid-url': 'This is not a valid link.',
  'not-https': 'The link must start with https://.',
  'has-credentials': 'The link must not contain a user name or password.',
  'non-default-port': 'The link must not contain a port number.',
  'host-not-allowed': `Widgets can only be loaded from ${ALLOWED_WIDGET_HOSTS.join(', ')}.`,
  'empty-param-name': 'Every widget parameter needs a name.',
};

type WidgetControlsProps = {
  selectedElement: AdElementWidget;
};

const WidgetControls: FC<WidgetControlsProps> = ({ selectedElement }) => {
  const updateElementData = useStore((state) => state.updateElementData);
  const { data } = selectedElement;
  // The typed link reaches the layer when the field loses focus or on Enter, not per keystroke:
  // the canvas draws the layer, and every new link reloads the widget page in its iframe.
  // Clicking Save takes the focus first, so a typed link is always saved.
  const [draftUrl, setDraftUrl] = useState(data.url);
  useEffect(() => setDraftUrl(data.url), [data.url]);
  // The same rule the served ad applies, so the reason shown here is why saving is refused.
  const resolved = resolveWidgetUrl({ ...data, url: draftUrl });

  const handleUrlChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setDraftUrl(event.target.value);
  };

  // A link that starts taking an event shows the ad's event by default: that is what a template
  // for every event wants. Only the change ticks the box, so a stored template is never changed
  // and a box unticked on an event link stays unticked.
  const commitUrl = () => {
    if (draftUrl === data.url) return;
    const startsTakingEvent = takesEvent(draftUrl) && !takesEvent(data.url);
    updateElementData(selectedElement.id, {
      ...data,
      url: draftUrl,
      params: startsTakingEvent ? withAdEvent(data.params, true) : data.params,
    });
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') commitUrl();
  };

  const isAdEventTicked = showsAdEvent(data);
  const linkTakesEvent = takesEvent(draftUrl);
  const handleAdEventChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateElementData(selectedElement.id, {
      ...data,
      params: withAdEvent(data.params, event.target.checked),
    });
  };

  return (
    <Flex direction="column" gap={6}>
      <FormControl isInvalid={!resolved.ok}>
        <FormLabel htmlFor="widget-url" fontSize="lg" fontWeight="semibold">
          Widget link
        </FormLabel>
        <Input
          id="widget-url"
          data-test-id="widget-url-input"
          placeholder={`https://${ALLOWED_WIDGET_HOSTS[0]}/…`}
          value={draftUrl}
          onChange={handleUrlChange}
          onBlur={commitUrl}
          onKeyDown={handleKeyDown}
        />
        {!resolved.ok && (
          <FormErrorMessage data-test-id="widget-url-problem">
            {URL_PROBLEMS[resolved.reason]}
          </FormErrorMessage>
        )}
      </FormControl>
      {/* Only a widget that takes an event can show the ad's. A ticked box stays, so it can be
          unticked after the link changes to another widget. */}
      {(linkTakesEvent || isAdEventTicked) && (
        <FormControl isInvalid={isAdEventTicked && !linkTakesEvent}>
          <Checkbox
            data-test-id="widget-ad-event"
            isChecked={isAdEventTicked}
            onChange={handleAdEventChange}
          >
            Show the ad&apos;s event
          </Checkbox>
          {linkTakesEvent ? (
            <FormHelperText>
              Served ads show the ad&apos;s event. The preview shows the widget&apos;s sample event.
            </FormHelperText>
          ) : (
            <FormErrorMessage data-test-id="widget-ad-event-problem">
              This widget doesn&apos;t take an event, so served ads would show the fallback image.
              Untick this box.
            </FormErrorMessage>
          )}
        </FormControl>
      )}
      {data.params.length > 0 && (
        <Flex direction="column">
          <Heading fontSize="lg" fontWeight="semibold" mb="2">
            Parameters
          </Heading>
          <List spacing={1} data-test-id="widget-params">
            {data.params.map((param) => (
              <ListItem key={param.name}>
                <Text as="span" fontFamily="mono" fontSize="sm">
                  {param.name} = {param.dataPoint ? "ad's event" : param.value}
                </Text>
              </ListItem>
            ))}
          </List>
        </Flex>
      )}
    </Flex>
  );
};

WidgetControls.displayName = 'WidgetControls';
export default WidgetControls;
